/**
 * Linked Accounts Management Page
 *
 * Route: /aa/accounts
 * Full management page for viewing and managing AA linked accounts.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { LinkedAccountsManager } from "@/components/aa/LinkedAccountsManager";
import { GlobalErrorFallback } from "@/components/error";
import { getAAStatus, aaQueryKeys } from "@/lib/aa-api";
import {
  ArrowLeft,
  Link as LinkIcon,
  Plus,
  RefreshCw,
  AlertCircle,
  Loader2,
} from "lucide-react";

export const Route = createFileRoute("/aa/accounts")({
  component: LinkedAccountsPage,
  errorComponent: GlobalErrorFallback,
});

function LinkedAccountsSkeleton() {
  return (
    <div className="space-y-4" role="status" aria-label="Loading linked accounts">
      {[1, 2].map((i) => (
        <div key={i} className="rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LinkedAccountsPage() {
  const {
    data: aaStatus,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: aaQueryKeys.status(),
    queryFn: getAAStatus,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const hasAccounts = (aaStatus?.linkedAccounts.length ?? 0) > 0;

  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5" />
              Linked Accounts
            </CardTitle>
            <div className="flex items-center gap-2">
              {hasAccounts && !isLoading && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="gap-2"
                >
                  {isFetching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Refresh
                </Button>
              )}
              <Button asChild size="sm" className="gap-2">
                <Link to="/aa">
                  <Plus className="h-4 w-4" />
                  Add Account
                </Link>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Loading */}
          {isLoading && <LinkedAccountsSkeleton />}

          {/* Error */}
          {isError && !isLoading && (
            <div className="flex flex-col items-center py-8 text-center" role="alert">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <h3 className="mt-4 text-sm font-medium">Failed to Load Accounts</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                We couldn't fetch your linked accounts. Please try again.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
                className="mt-4 gap-2"
              >
                {isFetching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Try Again
              </Button>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !isError && !hasAccounts && (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <LinkIcon className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="mt-4 text-sm font-medium">No Accounts Linked</h3>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Link your investment accounts via Account Aggregator to view and manage your portfolio.
              </p>
              <Button asChild size="sm" className="mt-4 gap-2">
                <Link to="/aa">
                  <Plus className="h-4 w-4" />
                  Link Accounts
                </Link>
              </Button>
            </div>
          )}

          {/* Accounts manager */}
          {!isLoading && !isError && hasAccounts && aaStatus && (
            <LinkedAccountsManager aaStatus={aaStatus} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
